// src/ui/GuestApp.jsx — what a person who does not have Allo sees.
//
// Mounted from App.jsx BEFORE the auth gate when the URL is /g/<token>, because
// a guest has no account and must never meet a login screen.
//
// ══ IT IS A REAL ALLO CHAT, NOT A LOOKALIKE ════════════════════════════════
// The guest publishes a device keypair on join (see guestSession.js), so from
// that moment they are simply another device to seal to. Everything here is the
// app's own plumbing, unchanged:
//
//     sendTextMessage / sendMediaMessage   the same send path the web uses
//     decryptMessage                       the same per-device decrypt
//     watchMessages                        the same live stream
//     orderMessages                        the same seq-anchored order
//
// The guest therefore gets real end-to-end encryption rather than a "temporary
// chat" that quietly is not, and the owner's phone needs no guest-specific code
// to talk to them.
//
// ══ WHAT IS DELIBERATELY MISSING ═══════════════════════════════════════════
// No sidebar, no chat list, no other conversations, no settings, no Vibes.
// A guest has exactly one thread, and every affordance that implies otherwise
// would be a door to nowhere.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  guestTokenFromLocation, peekGuestLink, joinGuestLink, loadGuestSession,
  endGuestSession, startGuestHeartbeat, ensureAnonUser,
  activateGuestIdentity, startGuestPresence, guestSessionConflict,
} from '../services/guestSession';
import GuestCall from './GuestCall';
import { watchMessages } from '../services/chats';
import { sendTextMessage, decryptMessage } from '../services/messages';
import { sendMediaMessage } from '../services/mediaUpload';
import { prepareFile } from '../services/mediaTools';
import { isDocFile, docStyle, humanBytes, pdfFirstPageThumb } from '../services/documents';
import { readablePoll, castVote, isPollSideEffect } from '../services/polls';
import {
  sendVoiceMessage, sendLocationMessage, currentPosition, waveformFromBlob, bestRecordingMime,
  reverseGeocode,
} from '../services/voiceAndPlace';
import { orderMessages, showsLateSendTime, setSelfUid } from '../services/messageOrder';
import { buildReplyFields, hydrateReplyTo, replySnippet } from '../services/replies';
import {
  REACTIONS, toggleReaction, deleteForMe, deleteForEveryone, isHidden,
  watchChatMeta, watermarkMs, msgMs,
} from '../services/guestActions';
import { reportDelivered, reportSeen } from '../services/receipts';
import { initSocket, getSocket, closeSocket } from '../services/socket';
import { watchPresence } from '../services/users';
import { getIdentity } from '../crypto/identity';
import SafeImg from './SafeImg';
import MediaViewer from './MediaViewer';
import StaticMap from './StaticMap';
import GuestCard from './GuestCard';
import LinkCard from './LinkCard';
import { firstUrl, cachedLink, fetchLink, normalizeLink } from '../services/links';
/* the same receipt glyph the rest of the web draws — see the note at its use */
import MessageTicks from './MessageTicks';
/* the durable send queue and the dispatcher that runs one job — see the notes
   at the top of each file for why they are two modules and not one */
import {
  hydrate as hydrateOutbox, watchOutbox, jobsFor, enqueue, runQueue,
  retire as retireJobs, cancel as cancelJob, retry as retryJob, FAILED,
} from '../services/guestOutbox';
import {
  performJob, jobAsMessage, replySeed, TEXT, MEDIA, DOCUMENT, VOICE, LOCATION,
} from '../services/guestSend';
import {
  Send, Paperclip, X, More, Image as ImageIcon, DocText, MapPin, Mic, Lock, Timer, Play,
  Reply, Smile, Trash, Alert, Download, Back, ChevronRight, Slash,
} from './icons';
import '../ui/media.css';   // the viewer's chrome lives here (mv-*), no gs-* collisions
import './guest.css';

/* ── the countdown, in words ──────────────────────────────────────────────── */
function leftLabel(expiresAt) {
  const ms = (expiresAt || 0) - Date.now();
  if (ms <= 0) return 'expired';
  const d = Math.floor(ms / 86400000);
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'} left`;
  const h = Math.floor(ms / 3600000);
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'} left`;
  return `${Math.max(1, Math.floor(ms / 60000))} min left`;
}

const timeOf = (t, late = false) => {
  const ms = !t ? Date.now() : (typeof t.toMillis === 'function' ? t.toMillis()
    : typeof t.seconds === 'number' ? t.seconds * 1000 : new Date(t).getTime());
  const s = new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  /* 🟢 the guest page orders by arrival like everywhere else, so it inherits the
     one cosmetic cost of that: a message that was slow to reach this browser sits
     below a reply sent after it, and only saying so keeps the clock readable. */
  return late ? `sent ${s}` : s;
};

/* ══ THE JOIN SCREEN ═══════════════════════════════════════════════════════
   Two fields and a warning. The warning is not boilerplate: this conversation
   really is deleted, and someone who does not know that will be upset later. */
function JoinScreen({ link, onJoin, busy, error }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);

  /* 🟢 THE AVATAR IS DOWNSCALED TO A DATA URL, not uploaded. A 72px JPEG is a
     couple of kilobytes — small enough to live on the user document — and it
     avoids giving an anonymous account write access to Storage for the sake of
     a thumbnail. */
  const pickPhoto = (file) => {
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const S = 144;
      const c = document.createElement('canvas');
      c.width = S; c.height = S;
      const ctx = c.getContext('2d');
      const side = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, S, S);
      setPhoto(c.toDataURL('image/jpeg', 0.72));
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  };

  const ready = name.trim().length > 1 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
    && (!link.needsPasscode || passcode.length > 0);

  return (
    <div className="gs-join">
      <div className="gs-join-card">
        <div className="gs-join-who">
          {link.ownerImage
            ? <SafeImg src={link.ownerImage} kind="inline" className="gs-join-ava" />
            : <span className="gs-join-ava gs-ph">{(link.ownerName || '?').charAt(0).toUpperCase()}</span>}
          <b>{link.ownerName}</b>
          <i>wants to chat with you on Allo</i>
        </div>

        <div className="gs-temp-note">
          <Timer width={15} height={15} />
          <span>
            This conversation is <b>temporary</b>. It and everything in it are deleted
            in <b>{leftLabel(link.expiresAt)}</b> — for both of you.
          </span>
        </div>

        <label className="gs-field">
          <span>Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Kelly" maxLength={40} autoFocus />
        </label>
        <label className="gs-field">
          <span>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="kelly@example.com" type="email" maxLength={120} />
          <em>Only used to let you know if a message arrives while you are away.</em>
        </label>

        {link.needsPasscode && (
          <label className="gs-field">
            <span>Passcode</span>
            <input value={passcode} onChange={(e) => setPasscode(e.target.value)} placeholder="••••" type="password" />
          </label>
        )}

        <div className="gs-photo-row">
          <button className="gs-photo" onClick={() => fileRef.current?.click()} type="button">
            {photo ? <img src={photo} alt="" /> : <ImageIcon width={17} height={17} />}
          </button>
          <span>Photo <i>optional</i></span>
          <input ref={fileRef} type="file" accept="image/*" hidden
            onChange={(e) => { pickPhoto(e.target.files?.[0]); e.target.value = ''; }} />
        </div>

        {error && <div className="gs-err">{error}</div>}

        <button
          className="gs-go" disabled={!ready || busy}
          onClick={() => onJoin({ name: name.trim(), email: email.trim(), passcode, photoUrl: photo })}
        >
          {busy ? 'Joining…' : 'Join the chat'}
        </button>

        <p className="gs-fine">
          <Lock width={12} height={12} /> End-to-end encrypted. Your messages are sealed to this
          browser, so opening the link somewhere else starts fresh.
        </p>
        {/* ⚖️ a guest is using Allo too — the same Terms, said before the button is pressed */}
        <p className="gs-fine gs-legal">
          By joining you agree to Allo's <a href="/terms" target="_blank" rel="noopener">Terms of Service</a> and
          acknowledge the <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>. Your email is only
          used to tell you a message is waiting, until this chat ends.
        </p>
      </div>
    </div>
  );
}

/* ══ VOICE PLAYER ══════════════════════════════════════════════════════════
   🟥 NOT `<audio controls>`. Two reasons, and the second is the one that showed
      up as "the container shows an error":

        · A WebM produced by MediaRecorder carries NO DURATION in its header —
          it is written as a stream — so the native controls read `Infinity`,
          render a broken scrubber, and can refuse to seek. The message document
          knows the real length because the recorder counted it; using that is
          both correct and free.

        · Native controls look like a browser, not like a chat. Every other
          surface in Allo draws a play button and a waveform.

   The element still does the decoding — it is just never shown. */
function VoicePlayer({ src, duration, waveform, mine }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const total = Number(duration) || 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onTime = () => setAt(el.currentTime || 0);
    const onEnd = () => { setPlaying(false); setAt(0); };
    const onErr = () => setPlaying(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    el.addEventListener('error', onErr);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('error', onErr);
    };
  }, [src]);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); return; }
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  /* seeking by tapping the waveform — using the KNOWN duration, because the
     element's own may be Infinity for a streamed WebM */
  const seek = (e) => {
    const el = ref.current;
    if (!el || !total) return;
    const box = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    try { el.currentTime = frac * total; setAt(frac * total); } catch { /* not seekable yet */ }
  };

  const bars = (waveform && waveform.length ? waveform : new Array(28).fill(0.35)).slice(0, 40);
  const done = total ? Math.min(1, at / total) : 0;
  const mmss = (sec) => `${Math.floor(sec / 60)}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

  return (
    <div className={`gs-vp ${mine ? 'mine' : ''}`}>
      <audio ref={ref} src={src} preload="metadata" />
      <button className="gs-vp-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? <span className="gs-vp-pause" /> : <Play width={15} height={15} />}
      </button>
      <div className="gs-vp-wave" onClick={seek} role="presentation">
        {bars.map((v, i) => (
          <i
            key={i}
            className={i / bars.length <= done ? 'on' : ''}
            style={{ height: `${Math.max(10, Math.round(v * 100))}%` }}
          />
        ))}
      </div>
      <span className="gs-vp-time">{mmss(playing || at ? at : total)}</span>
    </div>
  );
}

/* ══ DATES ═════════════════════════════════════════════════════════════════
   A separator per day, and the CURRENT day pinned to the top of the scroller.

   🟢 THE PIN IS `position: sticky` ON THE SEPARATOR ITSELF, not a floating
      element tracking scroll position. Every separator sticks to the top of the
      list and is pushed out by the next one, so the label on screen is always
      the day you are actually looking at — no scroll listener, no rAF, nothing
      to fall out of sync, and it stays right through a resize or a jump. */
function dayKeyOf(m) {
  const t = m?.timestamp;
  const ms = typeof t === 'number' ? t
    : t?.toMillis?.() ?? (t?.seconds ? t.seconds * 1000 : Date.parse(t) || 0);
  if (!ms) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabelOf(m) {
  const t = m?.timestamp;
  const ms = typeof t === 'number' ? t
    : t?.toMillis?.() ?? (t?.seconds ? t.seconds * 1000 : Date.parse(t) || 0);
  if (!ms) return '';
  const d = new Date(ms);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  const same = (a, b) => a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return 'Today';
  if (same(d, y)) return 'Yesterday';
  const thisYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString(undefined, {
    weekday: 'short', day: 'numeric', month: 'short', ...(thisYear ? {} : { year: 'numeric' }),
  });
}

/* ══ SKELETONS ═════════════════════════════════════════════════════════════
   🟢 SHAPED LIKE THE THING THEY REPLACE, and deliberately uneven: alternating
      sides and varied widths, because a column of identical grey rectangles
      reads as a broken layout rather than as a conversation arriving. They are
      inert — no shimmer sweep on more than the fill, nothing that outstays the
      content it stands in for. */
const SKELETON_ROWS = [
  { mine: false, w: 62 }, { mine: true, w: 44 }, { mine: false, w: 78 },
  { mine: false, w: 35 }, { mine: true, w: 66 }, { mine: true, w: 30 },
  { mine: false, w: 52 },
];

function Skeletons() {
  return (
    <div className="gs-skels" aria-hidden="true">
      {SKELETON_ROWS.map((r, i) => (
        <div className={`gs-row ${r.mine ? 'mine' : ''}`} key={i}>
          <div className={`gs-bub gs-skel ${r.mine ? 'mine' : ''}`} style={{ width: `${r.w}%` }}>
            <span className="gs-skel-line" />
            <span className="gs-skel-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══ ONE MESSAGE ═══════════════════════════════════════════════════════════
   Every type the owner can send is handled; anything genuinely unknown says so
   rather than rendering an empty bubble. */
/* 🟥 THE FIELD NAMES ARE decryptMessage's, NOT ONES I INVENTED. This bubble read
   `_url`, `_items`, `_mapUrl` and `_placeName` — none of which exist. What
   services/messages.js actually produces is:

     _plain      decrypted text
     _caption    decrypted caption
     _media      the decrypted blob URL for image / video / audio / document
     _docThumb   a document's own separately-sealed preview
     _mediaBatch [{ kind, url, needsLoad }] for an album
     _payload    the decrypted JSON for location / contact / poll

   Reading `_url` meant every media bubble got `undefined` and rendered its
   fallback — which is exactly the reported "Photo unavailable" on a photo that
   had arrived and decrypted perfectly well. It was never the photo. */
/* ══ THE MEDIA PAGE ═══════════════════════════════════════════════════════
   Everything shared in this conversation, newest first, under the day it was
   sent — reached from the ⋮ menu, which until now showed twelve thumbnails in a
   280px dropdown with no dates, no documents and nowhere to go.

   🟥 ONE ALBUM IS SEVERAL TILES. A message of type 'images' is one document
      carrying up to ten pictures; listing it as a single entry hides nine of
      them, and opening it would drop you at the first. Each item gets its own
      tile and its own index into the album, so tapping the seventh photo opens
      the viewer ON the seventh photo.

   🟢 DOCUMENTS SIT IN THE SAME GRID, not a second list. They were shared in the
      same conversation on the same day; splitting them into tabs means checking
      two places for "that thing they sent me on Tuesday". */
function mediaEntries(msgs, myUid) {
  const out = [];
  for (const m of msgs) {
    const mine = m.senderId === myUid;
    const base = { msg: m, mine, ts: msgMs(m) };
    if (m.type === 'images') {
      (m._mediaBatch || []).forEach((it, i) => {
        out.push({ ...base, key: `${m.id}_${i}`, index: i, kind: it.kind === 'video' ? 'video' : 'image', url: it.url || null });
      });
    } else if (m.type === 'image' || m.type === 'video') {
      out.push({ ...base, key: m.id, index: 0, kind: m.type, url: m._media || null });
    } else if (m.type === 'document') {
      out.push({ ...base, key: m.id, index: 0, kind: 'document', url: m._docThumb || null, name: m.documentName || 'Document' });
    }
  }
  /* newest first — the thing you are looking for is nearly always recent */
  return out.sort((a, b) => b.ts - a.ts);
}

/** Group into [{ label, items }] by calendar day, preserving the sort. */
function groupByDay(entries) {
  const days = [];
  let cur = null;
  for (const e of entries) {
    const key = dayKeyOf(e.msg);
    if (!cur || cur.key !== key) { cur = { key, label: dayLabelOf(e.msg), items: [] }; days.push(cur); }
    cur.items.push(e);
  }
  return days;
}

function MediaPage({ msgs, myUid, ownerName, meName, onOpen, onClose }) {
  const days = useMemo(() => groupByDay(mediaEntries(msgs, myUid)), [msgs, myUid]);
  const total = days.reduce((n, d) => n + d.items.length, 0);

  return (
    <div className="gs-mp" role="dialog" aria-label="Shared media">
      <header className="gs-mp-head">
        <button className="gs-icon" onClick={onClose} aria-label="Back"><Back width={20} height={20} /></button>
        <div className="gs-mp-title">
          <b>Shared media</b>
          <i>{total ? `${total} item${total === 1 ? '' : 's'}` : 'Nothing yet'}</i>
        </div>
      </header>

      <div className="gs-mp-body">
        {!total && (
          <div className="gs-mp-empty">
            <ImageIcon width={28} height={28} />
            <p>Photos, videos and files shared in this chat will appear here.</p>
          </div>
        )}

        {days.map((d) => (
          <section className="gs-mp-day" key={d.key || d.label}>
            <h3>{d.label}</h3>
            <div className="gs-mp-grid">
              {d.items.map((e) => (
                <button
                  type="button"
                  className={`gs-mp-tile ${e.kind}`}
                  key={e.key}
                  onClick={() => onOpen(e)}
                  title={`${e.mine ? meName : ownerName} · ${dayLabelOf(e.msg)}`}
                >
                  {e.kind === 'document' ? (
                    e.url
                      ? <img src={e.url} alt="" />
                      : (() => {
                        const { ext, color } = docStyle(e.name);
                        return (
                          <span className="gs-mp-doc" style={{ background: color }}>
                            <DocText width={20} height={20} />
                            <b>{ext || 'FILE'}</b>
                          </span>
                        );
                      })()
                  ) : e.url ? (
                    e.kind === 'video'
                      ? <video src={e.url} muted preload="metadata" />
                      : <img src={e.url} alt="" />
                  ) : (
                    /* 🟢 STILL DECRYPTING IS NOT MISSING. A tile that has not
                       resolved yet gets the shimmer the thread uses, not a
                       broken-image glyph that says the file is gone. */
                    <span className="gs-mp-wait" />
                  )}
                  {e.kind === 'video' && <span className="gs-play sm"><Play width={16} height={16} /></span>}
                  {e.kind === 'document' && <span className="gs-mp-name">{e.name}</span>}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

/* ══ A POLL ═══════════════════════════════════════════════════════════════
   🟥 A GUEST CAN VOTE BUT CANNOT ASK. Reading and answering a poll is ordinary
      participation — the owner asked a question and a guest is in the
      conversation to answer it. CREATING one is not: a poll outlives the reply
      it was sent in, is forwarded, and is tallied in a registry keyed by its
      original id, so a poll authored by an anonymous account that expires with
      its link would leave a permanent record nobody can attribute. There is no
      poll entry in the attachment sheet, and this component has no compose path
      — the restriction is the absence of code, not a disabled button.

   🟢 THE TALLY IS COMPUTED FROM `options[].votes`, NOT FROM A STORED COUNT. The
      counts on the document are a convenience the phone writes; the votes array
      is the fact. Deriving from the fact means a vote cast on the phone, a vote
      cast here, and a forwarded copy all agree without anybody reconciling. */
function PollBubble({ m, mine, myUid, onVote, busyOption }) {
  const poll = readablePoll(m);
  const opts = poll.options || [];
  const voters = new Set(opts.flatMap((o) => o.votes || []));
  const total = voters.size;
  const myPick = opts.filter((o) => (o.votes || []).includes(myUid)).map((o) => o.id);
  const voted = myPick.length > 0;

  if (poll.locked) {
    /* the sealed question never opened — say that, rather than drawing an
       answerable poll with no words in it */
    return (
      <div className="gs-poll">
        <div className="gs-poll-q gs-dim">🔒 This poll could not be opened</div>
      </div>
    );
  }

  return (
    <div className="gs-poll">
      <div className="gs-poll-top">
        <span className="gs-poll-tag">
          {poll.multipleChoice ? 'Select one or more' : 'Select one'}
        </span>
      </div>
      <div className="gs-poll-q">{poll.question || 'Poll'}</div>

      <div className="gs-poll-opts">
        {opts.map((o) => {
          const n = (o.votes || []).length;
          /* 🟢 THE BAR IS A SHARE OF THE VOTERS, NOT OF THE VOTES. In a
             multiple-choice poll the votes add up to more than the people, so a
             percentage of the total would happily print 140%. */
          const pct = total ? Math.round((n / total) * 100) : 0;
          const picked = myPick.includes(o.id);
          const busy = busyOption === o.id;
          return (
            <button
              type="button"
              key={o.id}
              className={`gs-poll-opt ${picked ? 'on' : ''} ${voted ? 'done' : ''}`}
              onClick={() => onVote?.(m, o)}
              disabled={busy}
              aria-pressed={picked}
            >
              {/* the fill sits behind the text, so a long option is still readable */}
              <span className="gs-poll-fill" style={{ width: `${voted ? pct : 0}%` }} />
              <span className="gs-poll-mark">{busy ? <span className="gs-st-spin" /> : (picked ? '✓' : '')}</span>
              <span className="gs-poll-txt">{o.text || 'Option'}</span>
              {voted && <span className="gs-poll-n">{n}</span>}
            </button>
          );
        })}
      </div>

      <div className="gs-poll-foot">
        {total === 0 ? 'No votes yet'
          : `${total} ${total === 1 ? 'person has' : 'people have'} voted`}
      </div>
    </div>
  );
}

/* ══ A DOCUMENT ═══════════════════════════════════════════════════════════
   🟥 A FILE IS NOT A ROW OF TEXT. This was an 18px glyph, a filename and nothing
      else — the same line whether it was a one-page receipt or a 40MB contract,
      and the first-page preview the SENDER had already rendered, uploaded and
      sealed was shown as a 34px square beside the name. The expensive part was
      being done and then thrown away.

      So the page leads: as wide as the bubble, at its real aspect ratio, and it
      opens the viewer. Underneath it, the three facts you need before deciding
      whether to download something — what it is, what it's called, how big — and
      one obvious action.

   🟢 AND A FILE WITH NO PAGE TO SHOW IS NOT A BROKEN VERSION OF ONE. A zip, a
      spreadsheet and a .docx have no first page this browser can render, so they
      get the coloured extension tile instead — a deliberate design, not a
      fallback that looks like a missing image. */
function DocBubble({ m, mine, onOpen }) {
  const name = m.documentName || 'Document';
  const { ext, color } = docStyle(name);
  const size = humanBytes(m.documentSize);
  const href = m._media || null;
  const page = m._docThumb || null;

  return (
    <div className="gs-docc">
      {page ? (
        <button
          type="button"
          className="gs-docc-page"
          onClick={() => onOpen?.(m, 0)}
          title="Open"
        >
          <img src={page} alt={`First page of ${name}`} />
          <span className="gs-docc-badge" style={{ background: color }}>{ext || 'FILE'}</span>
        </button>
      ) : (
        <div className="gs-docc-tile" style={{ background: color }}>
          <DocText width={26} height={26} />
          <b>{ext || 'FILE'}</b>
        </div>
      )}

      <div className="gs-docc-foot">
        <div className="gs-docc-meta">
          <div className="gs-docc-name" title={name}>{name}</div>
          <div className="gs-docc-sub">
            {[ext, size].filter(Boolean).join(' · ') || 'File'}
          </div>
        </div>
        {/* 🟢 `download` NEEDS A REAL HREF. While the bytes are still decrypting
            there is nothing to point at, so the button says so rather than being
            a link to '#' that quietly does nothing when pressed. */}
        {href ? (
          <a
            className="gs-docc-dl"
            href={href}
            download={name}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={`Download ${name}`}
            aria-label={`Download ${name}`}
          >
            <Download width={16} height={16} />
          </a>
        ) : (
          <span className="gs-docc-dl waiting" aria-label="Still decrypting">
            <span className="gs-st-spin" />
          </span>
        )}
      </div>
    </div>
  );
}

/* ══ THE STATE OF A MESSAGE THAT HAS NOT LANDED ═══════════════════════════
   🟥 THE TICKS ARE A LIE UNTIL THE MESSAGE EXISTS. A single grey check means
      "the server has this"; drawing one over a photo that is 12% uploaded says
      something untrue about where that photo is. So for anything still in the
      queue the receipt area is replaced outright: a ring while it climbs, a
      warning mark when it fails. Ticks resume the moment there is a real
      document to describe. */
function SendState({ job, onCancel, onRetry }) {
  if (!job) return null;
  const failed = job.status === FAILED;
  const pct = typeof job.pct === 'number' ? job.pct : null;

  if (failed) {
    return (
      <span className="gs-st gs-st-failed">
        <span className="gs-st-ico" title={job.error || 'Not sent'} aria-label="Not sent">
          <Alert width={13} height={13} />
        </span>
        <span className="gs-st-word">Not sent</span>
        <button type="button" className="gs-st-act" onClick={() => onRetry?.(job.id)}>Retry</button>
        <button type="button" className="gs-st-act ghost" onClick={() => onCancel?.(job.id)}>Delete</button>
      </span>
    );
  }

  /* 🟢 A PERCENTAGE ONLY WHERE ONE IS REAL. A text message and a pin have nothing
     to upload — their "progress" would be an animation measuring nothing — so
     they get a quiet clock instead. Media, documents and voice have a byte count
     and an honest ring. */
  if (pct === null || pct === 0) {
    return (
      <span className="gs-st">
        <span className="gs-st-spin" aria-label="Sending" />
        {onCancel && <button type="button" className="gs-st-x" onClick={() => onCancel(job.id)} aria-label="Cancel">
          <X width={11} height={11} />
        </button>}
      </span>
    );
  }
  return (
    <span className="gs-st">
      <span className="gs-st-ring" style={{ '--p': pct }} role="progressbar"
        aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <i>{pct}%</i>
      </span>
      <button type="button" className="gs-st-x" onClick={() => onCancel?.(job.id)} aria-label="Cancel">
        <X width={11} height={11} />
      </button>
    </span>
  );
}

function Bubble({
  m, mine, onOpen, onAct, actOpen, onReact, onReply, onDelete, canDeleteAll, quote,
  seen, delivered, onCancel, onRetry, myUid, onVote, busyOption, late,
}) {
  const job = m._outbox || null;
  const t = m.type;
  /* 🟥 normalizeLink COERCES WHATEVER ARRIVED. The phone sends this field as a
     JSON STRING and the web sends an object — reading it raw renders nothing for
     exactly half the senders. (And it is declared AFTER `t`: a const cannot be
     read before its own initialiser runs, so referencing `t` above this line is
     a TDZ crash, not a hoisting convenience.) */
  const linkMeta = (t === 'text' && m.linkMetadata) ? normalizeLink(m.linkMetadata) : null;
  const cap = m._caption || '';
  const loc = m._payload || {};
  const press = useRef(null);

  /* 🟢 LONG-PRESS ON TOUCH, HOVER ON A POINTER. `onContextMenu` alone is not
     enough — iOS Safari fires it inconsistently and Android fires it late — so
     the timer is explicit, and it is cancelled by a move so a scroll never opens
     a menu under the thumb. */
  const holdStart = () => {
    clearTimeout(press.current);
    press.current = setTimeout(() => onAct?.(m.id), 420);
  };
  const holdEnd = () => clearTimeout(press.current);

  if (m.isDeleted) {
    return (
      <div className={`gs-row ${mine ? 'mine' : ''}`}>
        <div className={`gs-bub ${mine ? 'mine' : ''} gs-gone`}>
          <p className="gs-txt gs-dim">This message was deleted</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`gs-row ${mine ? 'mine' : ''} ${actOpen ? 'acting' : ''}`
        + `${job ? ' queued' : ''}${job?.status === FAILED ? ' failed' : ''}`}
      onTouchStart={holdStart}
      onTouchEnd={holdEnd}
      onTouchMove={holdEnd}
      onContextMenu={(e) => { e.preventDefault(); onAct?.(m.id); }}
    >
      {/* ── the actions. On a desktop this appears on hover (CSS); on touch it is
             opened by the long-press above and shown by `.acting`. ── */}
      {/* 🟢 NO ACTIONS ON A MESSAGE THAT DOES NOT EXIST YET. Reacting to, replying
          to or deleting a queued bubble would all address a document id that no
          server has ever seen — the reaction would write to nothing and the reply
          would quote a message that may still fail. The failed state has its own
          Retry and Delete, which are the only two things that make sense there. */}
      <div className="gs-acts" hidden={!!job}>
        <button title="Reply" aria-label="Reply" onClick={() => onReply?.(m)}>
          <Reply width={15} height={15} />
        </button>
        <button title="React" aria-label="React" onClick={() => onAct?.(actOpen ? null : m.id)}>
          <Smile width={15} height={15} />
        </button>
        <button title="Delete" aria-label="Delete" onClick={() => onDelete?.(m)}>
          <Trash width={15} height={15} />
        </button>
      </div>

      {actOpen && (
        <div className="gs-emoji">
          {REACTIONS.map((e) => (
            <button key={e} onClick={() => onReact?.(m, e)}>{e}</button>
          ))}
        </div>
      )}

      <div className={`gs-bub ${mine ? 'mine' : ''} ${t === 'image' || t === 'video' ? 'media' : ''}`}>
        {/* the quoted message, if this is a reply */}
        {quote && (
          <div className="gs-quote">
            <b>{quote.who}</b>
            <span>{quote.text}</span>
          </div>
        )}
        {/* 🟢 A FORWARD SAYS SO. Both other clients label it, and without the label
            a forwarded photo reads as something this person took themselves. */}
        {m.forwarded && (
          <span className="gs-fwd"><Send width={11} height={11} />Forwarded</span>
        )}
        {t === 'text' || t === 'security' ? (
          <>
            <p className="gs-txt">{m._plain || m.text || ''}</p>
            {/* 🟢 `flatTop` SQUARES THE CARD'S TOP CORNERS so it meets the text
                above it as one object instead of a rounded slab floating inside
                a rounded bubble. Same treatment the main web chat gives it. */}
            {linkMeta && <LinkCard meta={linkMeta} flatTop />}
          </>
        ) : t === 'image' || t === 'sticker' || t === 'gif' ? (
          <>
            <button type="button" className="gs-media-btn" onClick={() => m._media && onOpen?.(m, 0)}>
              <SafeImg src={m._media} className="gs-media" label="Photo unavailable" />
            </button>
            {cap ? <p className="gs-txt">{cap}</p> : null}
          </>
        ) : t === 'video' ? (
          <>
            {m._media ? (
              /* the poster opens the viewer; the viewer is where it actually plays,
                 so a tap does not start audio inside a scrolling list */
              <button type="button" className="gs-media-btn gs-video-btn" onClick={() => onOpen?.(m, 0)}>
                <video className="gs-media" src={m._media} playsInline muted preload="metadata" />
                <span className="gs-play"><Play width={22} height={22} /></span>
              </button>
            ) : <div className="gs-loadable">Video</div>}
            {cap ? <p className="gs-txt">{cap}</p> : null}
          </>
        ) : t === 'audio' ? (
          m._media
            ? <VoicePlayer src={m._media} duration={m.duration} waveform={m.waveform} mine={mine} />
            : <span className="gs-voice-wait"><Mic width={14} height={14} /> Voice message</span>
        ) : t === 'poll' ? (
          <PollBubble m={m} mine={mine} myUid={myUid} onVote={onVote} busyOption={busyOption} />
        ) : t === 'document' ? (
          <DocBubble m={m} mine={mine} onOpen={onOpen} />
        ) : t === 'location' ? (
          /* 🟥 THE SEALED PAYLOAD IS `{lat, lng}`, not `{latitude, longitude}` —
             that is what the phone stringifies before sealing. ChatWindow accepts
             both spellings; so does this, so a message from either client opens. */
          (() => {
            const la = Number(loc.lat ?? loc.latitude);
            const ln = Number(loc.lng ?? loc.longitude);
            const ok = Number.isFinite(la) && Number.isFinite(ln);
            if (!ok) {
              return (
                <span className="gs-doc dead"><MapPin width={18} height={18} /><span>Location</span></span>
              );
            }
            return (
              <a className="gs-loc" target="_blank" rel="noreferrer"
                 href={`https://www.openstreetmap.org/?mlat=${la}&mlon=${ln}#map=16/${la}/${ln}`}>
                {/* the same OSM tile map the Allo web app draws — no key, no library */}
                <StaticMap lat={la} lng={ln} zoom={15} height={150} />
                <span className="gs-loc-foot">
                  <MapPin width={15} height={15} />
                  <span>
                    <b>{loc.name || 'Shared location'}</b>
                    <i>{la.toFixed(4)}, {ln.toFixed(4)}</i>
                  </span>
                </span>
              </a>
            );
          })()
        ) : t === 'images' ? (
          <>
            <div className="gs-album">
              {(m._mediaBatch || []).slice(0, 4).map((it, i) => (
                <button type="button" key={i} className="gs-media-btn" onClick={() => onOpen?.(m, i)}>
                  {it.kind === 'video'
                    ? <><video className="gs-album-item" src={it.url || undefined} playsInline muted preload="metadata" />
                        <span className="gs-play sm"><Play width={16} height={16} /></span></>
                    : <SafeImg src={it.url} kind="inline" className="gs-album-item" />}
                  {/* "+3" on the last tile when an album is bigger than the grid */}
                  {i === 3 && (m._mediaBatch || []).length > 4 && (
                    <span className="gs-more">+{(m._mediaBatch || []).length - 3}</span>
                  )}
                </button>
              ))}
            </div>
            {cap ? <p className="gs-txt">{cap}</p> : null}
          </>
        ) : (
          <p className="gs-txt gs-dim">{m._plain || m.text || 'Message'}</p>
        )}
        <span className="gs-meta">
          {/* the time is dimmed on its own — see the note on .gs-meta. Dimming the
              whole row took the read tick down with it. */}
          <i className="gs-time">{timeOf(m.timestamp, late)}</i>
          {/* 🟥 NOTHING WAS CLIPPING THE SECOND TICK — IT IS DRAWN THAT WAY.
              This used to be the `check-check` glyph from the icon set, whose
              second stroke is not a checkmark at all: it is a lone diagonal from
              (22,10) to (14.5,17.5), with no ascender and no elbow, floating in a
              square 24×24 box. Beside a first tick that runs from y=6 with a full
              corner, it reads as a tick with its top sliced off — which is
              exactly what it looked like. Widening the padding and the viewBox
              could never fix it; there was nothing outside the box to reveal.

              MessageTicks is the glyph the rest of the web already uses for
              exactly this: two COMPLETE checkmarks, the second offset 3.6 to the
              right, in a 14×11 box that is wider than it is tall the way a
              double tick actually is. Using it here also ends the split where
              the guest chat drew its receipts differently from every other
              surface in the product.

              🟢 COLOUR STAYS IN CSS. Both `color` and `readColor` are handed
              `currentColor`, so .gs-seen keeps owning the green (and its
              hairline drop-shadow against the blue bubble) rather than having a
              second, competing value baked into the markup. */}
          {mine && (job
            ? <SendState job={job} onCancel={onCancel} onRetry={onRetry} />
            : (
              <span className={`gs-ticks ${seen ? 'gs-seen' : ''}`}>
                <MessageTicks
                  status={seen ? 'read' : delivered ? 'delivered' : 'sent'}
                  size={16}
                  color="currentColor"
                  readColor="currentColor"
                />
              </span>
            ))}
        </span>
        {/* reactions sit on the bubble's edge, the way both other clients draw them */}
        {m.reactions && Object.keys(m.reactions).length > 0 && (
          <div className="gs-reacts">
            {Object.entries(m.reactions).map(([e, who]) => (
              <span key={e} title={`${(who || []).length}`}>{e}{(who || []).length > 1 ? (who || []).length : ''}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══ LOCAL PREVIEWS FOR QUEUED MESSAGES ═══════════════════════════════════
   🟥 A blob: URL BELONGS TO THE DOCUMENT THAT MADE IT. Storing one in the outbox
      and reading it back after a reload gives a URL the browser has never heard
      of — the bubble draws a broken image for a file that is sitting safely in
      IndexedDB. So the durable half is the File, and the URL is minted here,
      from the job, on whichever page load is currently showing it.

   🟢 AND REVOKED WHEN THE JOB GOES. Every createObjectURL holds its blob in
      memory until it is revoked; a guest who sends twenty photos over a long
      conversation would otherwise pin all twenty for the life of the tab. */
function useJobPreviews(jobs) {
  const cache = useRef(new Map());
  const [, bump] = useState(0);

  useEffect(() => {
    const live = new Set(jobs.map((j) => j.id));
    let changed = false;

    for (const j of jobs) {
      if (cache.current.has(j.id)) continue;
      const p = j.payload || {};
      const entry = { urls: [], thumb: null };
      try {
        if (p.files?.length) entry.urls = p.files.map((f) => URL.createObjectURL(f));
        else if (p.blob) entry.urls = [URL.createObjectURL(p.blob)];
        else if (p.file) entry.urls = [];
      } catch { /* a revoked or detached File — the bubble falls back to a glyph */ }
      cache.current.set(j.id, entry);
      changed = true;
    }
    for (const [id, entry] of cache.current) {
      if (live.has(id)) continue;
      entry.urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* fine */ } });
      cache.current.delete(id);
      changed = true;
    }
    if (changed) bump((n) => n + 1);
  }, [jobs]);

  // revoke everything on the way out — a closed tab is not a reason to leak
  useEffect(() => () => {
    for (const [, entry] of cache.current) {
      entry.urls.forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* fine */ } });
    }
    cache.current.clear();
  }, []);

  return cache.current;
}

/* ══ THE CHAT ══════════════════════════════════════════════════════════════ */
function GuestChat({ session, onExpired }) {
  const [raw, setRaw] = useState([]);
  /* 🟥 "NO MESSAGES YET" AND "NOT LOADED YET" LOOK IDENTICAL FROM `raw.length`,
     and that is the whole reason a reload showed an empty room for a second and
     then snapped full. The listener's first callback is the only thing that can
     tell them apart, so it is recorded explicitly. */
  const [gotFirst, setGotFirst] = useState(false);
  const [decoded, setDecoded] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [sheet, setSheet] = useState(false);         // the attachment chooser
  const [tray, setTray] = useState([]);              // picked files, pending review
  const [trayCap, setTrayCap] = useState('');        // one caption for the batch
  const [rec, setRec] = useState(null);              // { ms } while recording
  const [memo, setMemo] = useState(null);            // a finished recording, awaiting review
  const [acting, setActing] = useState(null);        // message id whose emoji row is open
  const [replyTo, setReplyTo] = useState(null);      // the message being answered
  const [quotes, setQuotes] = useState({});          // messageId → { who, text }
  const [place, setPlace] = useState(null);          // a fix awaiting confirmation
  const [chatMeta, setChatMeta] = useState(null);    // the chat doc: watermarks, expiry
  const recRef = useRef(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState('');
  const [menu, setMenu] = useState(false);
  const [typing, setTyping] = useState(false);
  const [presence, setPresence] = useState(null);
  const [expiresAt, setExpiresAt] = useState(session.expiresAt);
  const [busyPct, setBusyPct] = useState(null);
  /* 🟢 THE QUEUE IS THE SOURCE OF TRUTH FOR ANYTHING NOT YET SENT. `busyPct` and
     `sendErr` used to be it: ONE percentage and ONE error string for the whole
     composer, which is why a failed photo could only ever be reported as a
     sentence above the input box with no way to retry it and no idea which of
     three attachments it was about. Progress and failure now live on the message
     they belong to. */
  const [jobs, setJobs] = useState([]);
  const [link, setLink] = useState(null);            // { url, meta } for the composer strip
  const [voting, setVoting] = useState(null);        // { msgId, optionId } while a vote is in flight
  const [mediaPage, setMediaPage] = useState(false); // the full-screen "shared media" view
  /* 🟢 WHAT THE OWNER'S PRIVACY SETTINGS MEAN HERE, answered by the server on every
     ping — the guest cannot read the owner's user document and should not be able
     to. Defaults are permissive so a ping that has not landed yet looks like today
     rather than like a broken screen; the first beat arrives on mount. */
  const [gate, setGate] = useState({ online: true, lastSeen: true, receipts: true });
  const [blocked, setBlocked] = useState(false);
  const linkTimer = useRef(null);
  const linkDismissed = useRef(new Set());
  const listRef = useRef(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const docRef = useRef(null);
  const typingSentAt = useRef(0);
  /* The promise for "this browser is ready to encrypt". Held in a ref rather
     than state because send() must be able to AWAIT it — see the effect below. */
  const readyRef = useRef(null);

  const expired = (expiresAt || 0) <= Date.now();
  const chat = useMemo(() => ({
    id: session.chatId,
    participants: [session.ownerUid, session.guestUid],
    isGroup: false,
  }), [session]);

  /* 🟥 ARM THE BROWSER BEFORE IT CAN BE ASKED TO ENCRYPT ANYTHING.
     Two things the /g/ route misses by not living inside App.jsx's auth gate:
       · registerDevice(), without which ensureKeyPublished() returns false and
         every sealForUsers() throws — the dead send button;
       · presence, which only the phone ever published, so the owner could never
         see the guest as online or get a last-seen for them.
     Both are started here, once per session, and presence is torn down on the
     way out so leaving reads as offline immediately. */
  useEffect(() => {
    let dead = false;
    let stopPresence = () => {};
    readyRef.current = (async () => {
      const ok = await activateGuestIdentity(session);
      if (!dead) stopPresence = startGuestPresence(session.guestUid);
      return ok;
    })();
    return () => { dead = true; stopPresence(); };
  }, [session]);

  /* the live stream — the same one the web app uses for every other chat */
  useEffect(() => watchMessages(session.chatId, (rows) => {
    setRaw(rows);
    setGotFirst(true);
  }, { limitCount: 300 }), [session.chatId]);

  /* ══ THE OUTBOX ═══════════════════════════════════════════════════════════
     Anything the guest sends is queued first and leaves when Firestore has it.
     Three effects: mirror it into state, drain it, and retire what has landed. */
  const outScope = useMemo(
    () => ({ chatId: session.chatId, guestUid: session.guestUid }),
    [session.chatId, session.guestUid],
  );

  const pump = useCallback(() => {
    /* 🟢 A BLOCK STOPS THE QUEUE TOO. Anything already waiting stays waiting —
       it is not deleted, because an unblock later should be able to let it
       through — but nothing new is attempted while the door is shut. */
    if (blocked) return;
    runQueue(
      (job, hooks) => performJob(job, {
        chat,
        myUid: session.guestUid,
        myName: session.me?.name || 'Guest',
        participants: [session.ownerUid, session.guestUid],
      }, hooks),
      outScope,
    );
  }, [chat, session, outScope, blocked]);

  useEffect(() => watchOutbox((all) => setJobs(jobsFor(outScope.chatId, outScope.guestUid, all))),
    [outScope]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const ready = await hydrateOutbox(outScope.chatId, outScope.guestUid);
      if (dead) return;
      /* 🟢 A RESUMED QUEUE STILL WAITS FOR THE KEYS. readyRef is the promise that
         this browser can encrypt at all; draining before it settles would fail
         every queued job at once with "couldn't secure that" and turn a clean
         resume into a screen of red. */
      if (ready.length) { await readyRef.current; if (!dead) pump(); }
    })();
    return () => { dead = true; };
  }, [outScope, pump]);

  /* 🟥 RETIREMENT IS WHAT STOPS A MESSAGE BEING DRAWN TWICE. The optimistic row
     and the real document describe the same message and share a clientTempId;
     without this both are on screen at once. It also quietly cleans up the
     ambiguous reload case — a job interrupted mid-send whose message actually
     landed disappears here, with nobody having to press anything. */
  useEffect(() => {
    const live = new Set(raw.map((m) => m.clientTempId).filter(Boolean));
    if (live.size) retireJobs(live);
  }, [raw]);

  /* …decrypted, then ordered by the key both platforms agree on */
  useEffect(() => {
    let dead = false;
    (async () => {
      const out = [];
      /* 🟥 `vote_action` AND `reaction_action` LIVE IN THE MESSAGES COLLECTION BUT
         ARE NOT MESSAGES. They are how a vote or a reaction reaches another
         device without re-reading the poll. Rendered, each one becomes a blank
         bubble reading "Message" — so every tap on a poll option would leave
         litter in the conversation, on both sides. The phone skips them by type
         and so does the main web client; the guest page never learned to. */
      for (const m of raw) {
        if (isPollSideEffect(m)) continue;
        try { out.push(await decryptMessage(m, session.guestUid, { skipVideo: false })); }
        catch { out.push(m); }
      }
      if (dead) return;
      /* 🟥 ORDERING HAS TO KNOW WHOSE ROWS ARE MINE BEFORE IT STAMPS THEM. Arrival
         order keys my own sends to my send time and incoming ones to receive time;
         with no self uid every row looks incoming, so my own bubbles would be
         keyed to "now" and could be labelled as having arrived late to the device
         that sent them. This page has its own (anonymous) uid, so App.jsx's call
         never covers it. */
      try { setSelfUid(session.guestUid); } catch { /* ordering falls back to time */ }
      setMsgs(orderMessages(out));
      // decryption is the slow half — the skeletons stay until it is done, not
      // until the documents merely arrive
      setDecoded(true);
    })();
    return () => { dead = true; };
  }, [raw, session.guestUid]);

  const loading = !gotFirst || (raw.length > 0 && !decoded);

  /* 🟢 QUEUED MESSAGES ARE PART OF THE THREAD, NOT A BAR ABOVE THE COMPOSER.
     They sit where they were written — after everything already sent, in the
     order they were queued — so the conversation reads the way the person
     experienced writing it, and a failure is attached to the message it belongs
     to instead of being one shared sentence for the whole composer. */
  const localPreviews = useJobPreviews(jobs);
  const thread = useMemo(() => {
    const sent = msgs.filter((m) => !isHidden(m, session.guestUid));
    if (!jobs.length) return sent;
    return [...sent, ...jobs.map((j) => jobAsMessage(j, session.guestUid, localPreviews.get(j.id) || {}))];
  }, [msgs, jobs, localPreviews, session.guestUid]);

  /* 🟢 A QUOTE IS SEALED TOO, so it has to be opened before it can be shown —
     and against the REPLYING message's sender, not the quoted one. hydrateReplyTo
     already knows that; this just runs it for the rows that have a quote and
     caches the result by message id so scrolling does not re-open the same seal. */
  useEffect(() => {
    let dead = false;
    (async () => {
      const next = {};
      for (const m of msgs) {
        if (!m.replyTo || quotes[m.id]) continue;
        // eslint-disable-next-line no-await-in-loop
        const rt = await hydrateReplyTo(m).catch(() => null);
        if (!rt) continue;
        next[m.id] = {
          who: String(rt.senderId) === String(session.guestUid) ? 'You' : (session.ownerName || 'Allo user'),
          text: replySnippet(rt),
        };
      }
      if (!dead && Object.keys(next).length) setQuotes((q) => ({ ...q, ...next }));
    })();
    return () => { dead = true; };
    // `quotes` is intentionally not a dependency — it is the cache this fills
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs, session.guestUid, session.ownerName]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs.length]);

  /* 🟥 RECEIPTS — the guest reported NOTHING, which is both halves of the bug.
     The Allo side sat on one tick because nobody ever wrote `delivered` on their
     messages, and never went green because `readUpTo[guest]` — the only field a
     green tick is derived from — was never advanced.

     Deliberately the same two-step ChatWindow uses:

       · DELIVERED is not a read receipt. It fires for everything that reached
         this browser, whether or not the tab is in front, and it is not gated by
         anyone's privacy setting — exactly as the phone treats it.

       · SEEN waits for the tab to actually be VISIBLE. Marking a message read
         because a background tab happened to receive it is a lie told to the
         other person.

     🟢 AND THIS IS THE CATCH-UP TOO. It runs over the whole decrypted list on
        every change, so a guest who was offline while the owner wrote reports
        the backlog the moment the page comes back — `reportDelivered` no-ops on
        anything already marked, so the sweep is free. */
  useEffect(() => {
    const incoming = msgs.filter((m) => m.id && m.senderId !== session.guestUid);
    if (!incoming.length) return undefined;
    incoming.forEach((m) => reportDelivered(session.chatId, m, session.guestUid));

    /* 🟥 MUTUAL, SO THE GUEST STOPS PUBLISHING TOO. With the owner's receipts off
       the guest already sees no green ticks — the owner writes no watermark. But
       leaving the guest's own `seen` flags flowing would mean the owner keeps
       learning exactly when the guest read each message while giving nothing
       back, which is the opposite of what turning the setting off promises.
       Delivery is untouched: grey ✓✓ is not a read receipt, here or on the phone. */
    const markSeen = () => {
      if (!gate.receipts) return;
      incoming.forEach((m) => reportSeen(session.chatId, m, session.guestUid));
    };
    if (document.visibilityState === 'visible') { markSeen(); return undefined; }
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      markSeen();
      document.removeEventListener('visibilitychange', onVis);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
    /* 🟢 `gate.receipts` IS A DEPENDENCY. The first ping usually lands after the
       first messages do, so without it this effect would keep a closure over the
       permissive default and publish reads the owner had switched off — the same
       fail-open race readReceipts.js documents on the other side. */
  }, [msgs, session.chatId, session.guestUid, gate.receipts]);

  /* presence + typing, over the same socket the phones use */
  useEffect(() => {
    let stop = () => {};
    (async () => {
      const id = await getIdentity();
      initSocket(session.guestUid, id.deviceId);
      const s = getSocket();
      if (!s) return;
      const onTyping = (d) => { if (d?.chatId === session.chatId) setTyping(true); };
      const onStop = (d) => { if (d?.chatId === session.chatId) setTyping(false); };
      s.on('user-typing', onTyping);
      s.on('user-stopped-typing', onStop);
      stop = () => { s.off('user-typing', onTyping); s.off('user-stopped-typing', onStop); };
    })();
    return () => { stop(); closeSocket(); };
  }, [session.guestUid, session.chatId]);

  useEffect(() => watchPresence(session.ownerUid, setPresence), [session.ownerUid]);

  /* the chat document — where the read/delivered watermarks live */
  useEffect(() => watchChatMeta(session.chatId, setChatMeta), [session.chatId]);

  /* 🟢 WHAT THE OWNER HAS READ, as one number. Everything I sent at or before
     this instant has been read, whichever of their devices did the reading and
     whether or not the per-message `seen` write ever reached that document. */
  const ownerReadMs = watermarkMs(chatMeta, session.ownerUid, 'readUpTo');
  const ownerGotMs = Math.max(
    watermarkMs(chatMeta, session.ownerUid, 'deliveredUpTo'),
    ownerReadMs,                       // read implies delivered
  );

  /* 🟢 SAY WHY A TICK IS THE COLOUR IT IS. There are exactly three inputs and no
     way to see any of them from the outside, which is how "the ticks don't turn
     green" stays unanswerable. Printed once per change, and it settles it:

       ownerReadUpTo === 0 AND every seen === false
         → the other side is not publishing read receipts at all. That is a
           SETTING, not a fault: Allo only writes `readUpTo` when read receipts
           are on (it is the sender's receipt, so switching them off has to
           withhold it). Check Privacy → Read receipts on the Allo account.

       ownerReadUpTo > 0 but older than the message
         → they have not actually read that one yet. Working correctly.

       seen === true but the tick is grey
         → then it is a rendering bug and it is mine. */
  const lastTickLog = useRef('');
  useEffect(() => {
    const mineRows = msgs.filter((m) => m.senderId === session.guestUid);
    if (!mineRows.length) return;
    const iso = (ms) => (ms ? new Date(ms).toISOString().slice(11, 19) : 'never');
    /* 🟢 ONE FLAT STRING, NOT AN OBJECT. A console object collapses to `[{…}]`
       when it is copied out of the devtools, which is exactly the part worth
       reading — and this fires on every render, so it also has to stop repeating
       itself or it buries everything else. */
    const line = mineRows.slice(-3).map((m) => {
      const at = msgMs(m);
      const green = !!m.seen || (ownerReadMs > 0 && at <= ownerReadMs);
      return `${iso(at)} seen=${m.seen ? 'Y' : 'n'} wm=${ownerReadMs > 0 && at <= ownerReadMs ? 'Y' : 'n'} → ${green ? 'GREEN' : 'grey'}`;
    }).join('  |  ');
    const full = `[guest] ticks — ownerReadUpTo=${iso(ownerReadMs)}  ::  ${line}`;
    if (full === lastTickLog.current) return;      // nothing changed; say nothing
    lastTickLog.current = full;
    console.log(full);
  }, [msgs, chatMeta, ownerReadMs, ownerGotMs, session.guestUid]);

  useEffect(() => {
    if (!typing) return undefined;
    const id = setTimeout(() => setTyping(false), 4000);
    return () => clearTimeout(id);
  }, [typing]);

  /* the heartbeat doubles as the expiry watch: the page locks the moment the
     link ends, not at the next reload */
  useEffect(() => startGuestHeartbeat(session, (state) => {
    if (state?.expiresAt) setExpiresAt(state.expiresAt);
    if (state?.expired) onExpired();
    if (state?.gate) setGate((g) => ({ ...g, ...state.gate }));
    if (typeof state?.blocked === 'boolean') setBlocked(state.blocked);
  }), [session, onExpired]);

  /* 🟢 THE LINK STRIP. Detection is a regex and runs on every keystroke because it
     costs nothing; only the LOOKUP is debounced, and 400ms is deliberately longer
     than it needs to be. A URL pasted and sent in one motion should never cost a
     request — if you pause, the card appears; if you don't, the send uses whatever
     is already cached and moves on without waiting.

     🟥 AND A STALE CARD IS WORSE THAN NO CARD. The composer can move on while the
     lookup is in flight, so the result is only accepted if the text still points
     at the same URL — otherwise you would attach a preview of the link you just
     deleted to the message you are writing now. */
  useEffect(() => {
    clearTimeout(linkTimer.current);
    const url = firstUrl(draft);
    if (!url || linkDismissed.current.has(url)) { setLink(null); return undefined; }

    const hit = cachedLink(url);
    if (hit) { setLink({ url, meta: hit }); return undefined; }
    if (hit === null) { setLink(null); return undefined; }   // known to have no preview

    let live = true;
    linkTimer.current = setTimeout(async () => {
      const meta = await fetchLink(url);
      if (live && meta && firstUrl(draft) === url) setLink({ url, meta });
    }, 400);
    return () => { live = false; clearTimeout(linkTimer.current); };
  }, [draft]);

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - typingSentAt.current < 2500) return;
    typingSentAt.current = now;
    try {
      getSocket()?.emit('start-typing', {
        fromUserId: session.guestUid, fromUserName: session.me?.name || 'Guest',
        toUserId: session.ownerUid, chatId: session.chatId, isGroup: false,
      });
    } catch {}
  }, [session]);

  const send = async () => {
    const text = draft.trim();
    if (!text || expired || blocked) return;
    setDraft('');
    setSendErr('');
    /* 🟥 QUEUE, DON'T AWAIT. This used to hold the composer disabled behind the
       network round-trip and then, on failure, put the words BACK in the input
       box — which loses the reply it was attached to, loses its place in the
       conversation, and asks the person to notice a sentence above the keyboard.
       A queued message is on screen as a bubble, in order, with its own retry. */
    const seed = replyTo ? replySeed(replyTo) : null;
    setReplyTo(null);
    /* 🟢 WHATEVER IS ALREADY CACHED, NOT WHATEVER IS ON SCREEN. The strip may not
       have appeared yet — pasting and sending in one motion is the common case —
       and the send must not wait for a lookup. A dismissed URL stays dismissed. */
    const url = firstUrl(text);
    const linkMetadata = url && !linkDismissed.current.has(url)
      ? (cachedLink(url) || (link?.url === url ? link.meta : null)) : null;
    setLink(null);
    await enqueue({
      id: `gq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      chatId: session.chatId,
      guestUid: session.guestUid,
      kind: TEXT,
      payload: { text, replySeed: seed, linkMetadata: linkMetadata || null },
    });
    try {
      getSocket()?.emit('stop-typing', {
        fromUserId: session.guestUid, toUserId: session.ownerUid, chatId: session.chatId,
      });
    } catch {}
    await readyRef.current;
    pump();
  };

  /* 🟥 TWO SENDERS, NOT ONE — and the old code used the wrong one for half of
     what its own file picker accepted.

     `sendMediaMessage` writes a message whose type is 'video' when the item says
     video and 'image' for EVERYTHING ELSE. Handing it a PDF therefore produced an
     image message carrying a PDF, which no client can render. Documents have
     their own sender, with its own fields and its own sealed preview.

     The photo path was wrong in a quieter way: it read the file's raw bytes
     itself instead of going through `prepareFile`, which is what every other web
     surface uses and what produces `width`, `height` and the base64 thumbnail
     that the receiver renders as a blur while the real bytes decrypt. Without
     them a guest's photo arrives with no dimensions and no placeholder — the
     bubble has nothing to draw until the download finishes. prepareFile also
     handles the formats a bare arrayBuffer() cannot: it re-encodes exotic types
     and gives a clear error for HEIC rather than uploading something undecodable. */
  /* 🟢 PICKING IS NOT SENDING. Files go into a review tray first — a thumbnail
     strip, a caption box, and a chance to drop one — because a file picker on a
     phone is easy to fumble and an unsent photo is recoverable while a sent one
     is not. Desktop gets the same tray; there is no reason for the two to differ
     and one code path is one set of bugs. */
  /* 🟥 A TRAY ITEM IS AN OBJECT, NOT THE File ITSELF. The old code called
     Object.assign(file, { previewUrl }) — mutating the File the browser handed
     us, which is both a lie about an immutable object and impossible to extend:
     there is nowhere to put a first-page render that arrives 300ms later without
     mutating it again and hoping React notices. A wrapper has room for both, and
     replacing the wrapper is a state change React can actually see. */
  const stageFiles = (files) => {
    const list = Array.from(files || []).slice(0, 10);
    if (!list.length || expired || blocked) return;
    setSheet(false);
    const wrapped = list.map((f) => ({
      file: f,
      key: `${f.name}_${f.size}_${f.lastModified || 0}_${Math.random().toString(36).slice(2, 6)}`,
      previewUrl: (f.type?.startsWith('image/') || f.type?.startsWith('video/'))
        ? URL.createObjectURL(f) : null,
      page: null,            // a PDF's first page, once it has rendered
      pageBusy: isDocFile(f) && /pdf$/i.test(f.type || f.name || ''),
    }));
    setTray((prev) => [...prev, ...wrapped].slice(0, 10));

    /* 🟢 THE PAGE IS RENDERED FOR THE PREVIEW, NOT JUST FOR THE MESSAGE. The
       sender's own copy of this render was already being made — but only inside
       sendDocumentMessage, AFTER the decision to send. Doing it here as well
       means you see what you are about to send, which is the entire point of a
       review tray, and it costs one canvas draw of a page that is already in
       memory. */
    wrapped.forEach((w) => {
      if (!w.pageBusy) return;
      pdfFirstPageThumb(w.file, { maxWidth: 220 })
        .then((thumb) => {
          if (!thumb?.bytes?.length) { markTray(w.key, { pageBusy: false }); return; }
          const url = URL.createObjectURL(new Blob([thumb.bytes], { type: 'image/png' }));
          markTray(w.key, { page: url, pageBusy: false });
        })
        .catch(() => markTray(w.key, { pageBusy: false }));
    });
  };

  /* patch one tray row by key — index is not stable, the user can drop a row
     while a page is still rendering */
  const markTray = useCallback((key, fields) => {
    setTray((prev) => prev.map((w) => (w.key === key ? { ...w, ...fields } : w)));
  }, []);

  const freeTrayItem = (w) => {
    try { if (w?.previewUrl) URL.revokeObjectURL(w.previewUrl); } catch { /* fine */ }
    try { if (w?.page) URL.revokeObjectURL(w.page); } catch { /* fine */ }
  };

  const dropFromTray = (i) => setTray((prev) => {
    const next = prev.slice();
    const [gone] = next.splice(i, 1);
    freeTrayItem(gone);
    return next;
  });

  const clearTray = useCallback(() => {
    setTray((prev) => { prev.forEach(freeTrayItem); return []; });
    setTrayCap('');
  }, []);

  const sendTray = async () => {
    if (!tray.length || expired || blocked) return;
    setSendErr('');
    const caption = trayCap.trim();
    const files = tray.slice();
    const seed = replyTo ? replySeed(replyTo) : null;
    setReplyTo(null);
    clearTray();

    /* 🟥 DOCUMENTS ARE THEIR OWN MESSAGES, PHOTOS SHARE ONE. That was already
       true of the send path; queueing has to preserve it or a PDF picked
       alongside two photos would be uploaded as an image nobody can open. */
    const docs = files.filter((w) => isDocFile(w.file)).map((w) => w.file);
    const shots = files.filter((w) => !isDocFile(w.file)).map((w) => w.file);
    const stamp = () => `gq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    if (shots.length) {
      await enqueue({
        id: stamp(),
        chatId: session.chatId,
        guestUid: session.guestUid,
        kind: MEDIA,
        payload: { files: shots, caption, replySeed: seed },
      });
    }
    /* 🟢 the quote goes on the FIRST message only — five files dropped while
       replying should not repeat the same quote block five times */
    let carry = shots.length ? null : seed;
    for (const f of docs) {
      // eslint-disable-next-line no-await-in-loop
      await enqueue({
        id: stamp(),
        chatId: session.chatId,
        guestUid: session.guestUid,
        kind: DOCUMENT,
        payload: { file: f, name: f.name, size: f.size, replySeed: carry },
      });
      carry = null;
    }
    await readyRef.current;
    pump();
  };

  /* 📍 CURRENT POSITION ONLY — see currentPosition() for why live sharing is not
     offered from a browser tab. */
  /* 🟢 A FIX IS SHOWN BEFORE IT IS SHARED. Where you are is not something to send
     on a single tap and find out about afterwards — the preview draws the actual
     pin and names the place, so what you approve is what the other person gets.
     The name is looked up in the background; the map does not wait for it. */
  const pickPlace = async () => {
    setSheet(false);
    setSendErr('');
    setPlace({ loading: true });
    try {
      const pos = await currentPosition();
      setPlace({ lat: pos.lat, lng: pos.lng, name: null, naming: true });
      const name = await reverseGeocode(pos.lat, pos.lng);
      setPlace((p) => (p && p.lat === pos.lat ? { ...p, name, naming: false } : p));
    } catch (e) {
      setPlace(null);
      setSendErr(e?.message || 'Could not get your location.');
    }
  };

  const sendPlace = async () => {
    const p = place;
    if (!p?.lat || blocked) return;
    setPlace(null);
    setSendErr('');
    const seed = replyTo ? replySeed(replyTo) : null;
    setReplyTo(null);
    await enqueue({
      id: `gq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      chatId: session.chatId,
      guestUid: session.guestUid,
      kind: LOCATION,
      payload: { lat: p.lat, lng: p.lng, name: p.name || null, replySeed: seed },
    });
    await readyRef.current;
    pump();
  };

  /* 🎤 VOICE MEMO.
     🟥 THE STREAM'S TRACKS ARE STOPPED EXPLICITLY on every exit path. Leaving
        them live keeps the browser's recording indicator lit and the microphone
        held open — on a phone that is both alarming and a battery cost, and it
        is exactly what gets forgotten in the cancel path. */
  const startRec = async () => {
    if (rec || expired || blocked) return;
    setSendErr('');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setSendErr('Microphone permission was denied — allow it in your browser settings.');
      return;
    }
    const mime = bestRecordingMime();
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    const parts = [];
    mr.ondataavailable = (e) => { if (e.data && e.data.size) parts.push(e.data); };
    const startedAt = Date.now();
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* fine */ } });
      const secs = Math.round((Date.now() - startedAt) / 1000);
      const cancelled = recRef.current?.cancelled;
      recRef.current = null;
      setRec(null);
      if (cancelled || secs < 1 || !parts.length) return;   // a tap is not a memo
      /* 🟢 STOPPING IS NOT SENDING. A voice memo is the one thing you cannot skim
         before it goes, so it lands in a preview first — play it back, keep it or
         throw it away. The waveform is computed now rather than at send time so
         the preview shows the same bar the recipient will see. */
      const blob = new Blob(parts, { type: mime || 'audio/webm' });
      const waveform = await waveformFromBlob(blob);
      setMemo({ blob, secs, waveform, url: URL.createObjectURL(blob) });
    };
    recRef.current = { mr, cancelled: false };
    mr.start();
    setRec({ ms: 0 });
  };

  const dropMemo = useCallback(() => {
    setMemo((m) => { try { if (m?.url) URL.revokeObjectURL(m.url); } catch { /* fine */ } return null; });
  }, []);

  const sendMemo = async () => {
    if (!memo || blocked) return;
    const m = memo;
    setMemo(null);
    setSendErr('');
    const seed = replyTo ? replySeed(replyTo) : null;
    setReplyTo(null);
    /* 🟢 THE BLOB GOES IN THE QUEUE, so a memo survives a reload mid-upload and
       Retry re-sends the actual recording rather than an empty bubble. The
       preview's object URL is revoked here — the bubble makes its own from the
       stored blob, which is the copy that outlives this component. */
    await enqueue({
      id: `gq_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      chatId: session.chatId,
      guestUid: session.guestUid,
      kind: VOICE,
      payload: { blob: m.blob, secs: m.secs, waveform: m.waveform || [], replySeed: seed },
    });
    try { URL.revokeObjectURL(m.url); } catch { /* fine */ }
    await readyRef.current;
    pump();
  };

  const stopRec = (cancelled) => {
    const r = recRef.current;
    if (!r) return;
    r.cancelled = !!cancelled;
    try { r.mr.stop(); } catch { /* onstop still runs the cleanup */ }
  };

  // the running counter, and the hard stop that keeps a forgotten tab from
  // recording forever
  useEffect(() => {
    if (!rec) return undefined;
    const id = setInterval(() => {
      setRec((r) => {
        if (!r) return r;
        const ms = r.ms + 250;
        if (ms >= 5 * 60 * 1000) stopRec(false);   // 5 minutes is a memo, not a podcast
        return { ms };
      });
    }, 250);
    return () => clearInterval(id);
  }, [rec]);

  // leaving the page mid-recording must not leave the mic open
  useEffect(() => () => { try { stopRec(true); } catch { /* fine */ } }, []);

  /* Build the shape MediaViewer already speaks, so the guest gets the same
     viewer the Allo web app uses — zoom, arrows, filmstrip, download, and the
     sender + timestamp in the title bar — rather than a second, lesser one. */
  const openViewer = useCallback((m, index) => {
    const mine = m.senderId === session.guestUid;
    /* 🟢 A DOCUMENT OPENS TOO. Its first page is what the stage shows — the same
       render the bubble draws — while the download button saves the REAL file
       under its REAL name, not a PNG of page one. `fileName`/`downloadUrl` on the
       item are what tell the viewer to do that (see MediaViewer). */
    const items = m.type === 'document'
      ? [{
        url: m._docThumb || null,
        kind: 'image',
        mime: 'image/png',
        fileName: m.documentName || 'file',
        downloadUrl: m._media || null,
      }]
      : m.type === 'images'
        ? (m._mediaBatch || []).map((it) => ({ url: it.url, kind: it.kind, mime: null }))
        : [{ url: m._media, kind: m.type === 'video' ? 'video' : 'image', mime: null }];
    /* a document with no page render is still worth opening — the download is
       the point — so it passes when it has a file even with no preview */
    const openable = items.some((it) => it.url || it.downloadUrl);
    if (!openable) return;
    setViewer({
      items,
      index: index || 0,
      mine,
      senderName: mine ? (session.me?.name || 'You') : (session.ownerName || 'Allo user'),
      timestamp: m.timestamp,
      caption: m._caption || '',
    });
  }, [session]);

  /* 🟢 ONE VOTE IN FLIGHT AT A TIME, and it is identified by MESSAGE AND OPTION
     rather than by a bare boolean — two polls can be on screen, and marking "a
     vote is happening" globally would spin every option in the thread. */
  const vote = useCallback(async (m, option) => {
    if (expired || blocked || voting) return;
    setVoting({ msgId: m.id, optionId: option.id });
    try {
      await readyRef.current;
      await castVote({
        chat,
        myUid: session.guestUid,
        myName: session.me?.name || 'Guest',
        message: m,
        optionId: option.id,
        optionText: option.text,
        /* the shared poll registry may be closed to an anonymous account; a vote
           must not be lost to that — see the note in castVote */
        registryOptional: true,
      });
    } catch (e) {
      setSendErr(e?.message || 'Could not record your vote.');
      console.warn('[guest] vote failed:', e?.message);
    }
    setVoting(null);
  }, [chat, session, expired, voting]);

  const react = useCallback(async (m, emoji) => {
    setActing(null);
    try {
      await toggleReaction({
        chatId: session.chatId, message: m, myUid: session.guestUid,
        myName: session.me?.name || 'Guest', emoji, toUserId: session.ownerUid,
      });
    } catch (e) {
      setSendErr(e?.message || 'That reaction did not send.');
    }
  }, [session]);

  const removeMsg = useCallback(async (m) => {
    setActing(null);
    const mineMsg = m.senderId === session.guestUid;
    /* Delete for everyone is only ever offered for your OWN message — the
       confirm names which of the two is about to happen, because they are not
       remotely the same thing. */
    const forAll = mineMsg && window.confirm(
      'Delete this message for everyone?\n\nOK removes it for both of you. Cancel removes it only for you.',
    );
    try {
      if (forAll) {
        await deleteForEveryone({
          chatId: session.chatId, messageId: m.id, myUid: session.guestUid, senderId: m.senderId,
        });
      } else {
        await deleteForMe({ chatId: session.chatId, messageId: m.id, myUid: session.guestUid });
      }
    } catch (e) {
      setSendErr(e?.message || 'Could not delete that message.');
    }
  }, [session]);

  const media = useMemo(() => msgs.filter((m) => ['image', 'video', 'images'].includes(m.type)), [msgs]);
  const links = useMemo(() => msgs.filter((m) => /https?:\/\//i.test(m._plain || m.text || '')), [msgs]);
  const docs = useMemo(() => msgs.filter((m) => m.type === 'document'), [msgs]);

  return (
    <div className="gs-shell">
      <header className="gs-head">
        {session.ownerImage
          ? <SafeImg src={session.ownerImage} kind="inline" className="gs-head-ava" />
          : <span className="gs-head-ava gs-ph">{(session.ownerName || '?').charAt(0).toUpperCase()}</span>}
        <div className="gs-head-id">
          <b>{session.ownerName}</b>
          {/* 🟥 THE OWNER'S PRIVACY DECIDES WHAT THIS LINE MAY SAY. It used to print
              their online state and last-seen time unconditionally — the one
              surface in the product where "Last seen: nobody" did not apply,
              reachable by anyone holding a link. With both hidden the line is
              blank rather than "offline": claiming they are offline is a
              statement about them we are not allowed to make. Typing survives,
              because it is about THIS conversation and they chose to type in
              it. */}
          <i>
            {typing ? 'typing…'
              : (gate.online && presence?.online) ? 'online'
                : (gate.lastSeen && presence?.lastSeen)
                  ? `last seen ${new Date(presence.lastSeen).toLocaleString()}`
                  : (gate.online || gate.lastSeen) ? 'offline' : ''}
          </i>
        </div>
        <span className={`gs-chip ${expired ? 'dead' : ''}`}><Timer width={12} height={12} />{leftLabel(expiresAt)}</span>
        <button className="gs-icon" onClick={() => setMenu((v) => !v)} aria-label="Menu"><More width={18} height={18} /></button>
        {menu && (
          <div className="gs-menu" onMouseLeave={() => setMenu(false)}>
            {/* 🟢 THE DROPDOWN IS A DOOR NOW, NOT A DESTINATION. Twelve thumbnails in
                a 280px panel with no dates, no documents and nowhere to go was the
                whole of "shared media"; the row below opens the real page. */}
            <button type="button" className="gs-menu-row" onClick={() => { setMenu(false); setMediaPage(true); }}>
              <span className="gs-menu-row-ico"><ImageIcon width={17} height={17} /></span>
              <span className="gs-menu-row-txt">
                <b>Media, files and links</b>
                <i>{media.length + docs.length ? `${media.length + docs.length} shared` : 'Nothing yet'}</i>
              </span>
              <ChevronRight width={16} height={16} />
            </button>
            <div className="gs-menu-h">Recent <i>{media.length}</i></div>
            <div className="gs-menu-media">
              {media.slice(0, 8).map((m, i) => (
                // …and the media panel reads the same real field as the bubble
                <span key={i}>{m._media ? <SafeImg src={m._media} kind="inline" /> : <span className="gs-ph" />}</span>
              ))}
              {media.length === 0 && <em>Nothing yet</em>}
            </div>
            <div className="gs-menu-h">Links <i>{links.length}</i></div>
            <div className="gs-menu-links">
              {links.slice(0, 8).map((m, i) => {
                const url = (m._plain || m.text || '').match(/https?:\/\/\S+/i)?.[0];
                return <a key={i} href={url} target="_blank" rel="noreferrer">{url}</a>;
              })}
              {links.length === 0 && <em>Nothing yet</em>}
            </div>
          </div>
        )}
      </header>

      <div className="gs-list" ref={listRef} onClick={() => acting && setActing(null)}>
        {/* the same card the owner sees, minus everything that is theirs alone:
            no link record, no passcode, no note — the guest cannot read that row
            and has no reason to. */}
        <GuestCard
          chat={{ isGuest: true, expiresAt }}
          link={null}
          who={session.ownerName}
          mine={false}
        />

        {/* 🟢 THE SKELETONS ARE NOT UNMOUNTED THE INSTANT THE DATA LANDS. They keep
            their space and fade, while the real bubbles fade in over them — the
            layout never jumps, which is the entire point of having them. */}
        {loading && <Skeletons />}

        <div className={`gs-msgs ${loading ? 'waiting' : 'ready'}`}>
          {thread.map((m, i, arr) => {
            const key = dayKeyOf(m);
            const newDay = key && key !== dayKeyOf(arr[i - 1]);
            return (
              <React.Fragment key={m.id}>
                {newDay && <div className="gs-day"><span>{dayLabelOf(m)}</span></div>}
                {/* 🟢 arr[i - 1] is the row drawn directly ABOVE in this oldest-first
                    list — the only neighbour that can make this bubble's time read
                    backwards. */}
                <Bubble
                  m={m}
                  late={showsLateSendTime(m, arr[i - 1])}
                  mine={m.senderId === session.guestUid}
                  onOpen={openViewer}
                  onAct={setActing}
                  actOpen={acting === m.id}
                  onReact={react}
                  onReply={(x) => { setActing(null); setReplyTo(x); }}
                  onDelete={removeMsg}
                  canDeleteAll={m.senderId === session.guestUid}
                  quote={quotes[m.id]}
                  seen={gate.receipts && (!!m.seen || (ownerReadMs > 0 && msgMs(m) <= ownerReadMs))}
                  delivered={!!m.delivered || (ownerGotMs > 0 && msgMs(m) <= ownerGotMs)}
                  onCancel={cancelJob}
                  onRetry={(id) => { retryJob(id); pump(); }}
                  myUid={session.guestUid}
                  onVote={vote}
                  busyOption={voting?.msgId === m.id ? voting.optionId : null}
                />
              </React.Fragment>
            );
          })}
        </div>

        {!loading && !thread.length && (
          <div className="gs-empty-thread">No messages yet — say hello.</div>
        )}
        {typing && <div className="gs-row"><div className="gs-bub gs-typing"><i /><i /><i /></div></div>}
      </div>

      {mediaPage && (
        <MediaPage
          msgs={msgs}
          myUid={session.guestUid}
          ownerName={session.ownerName || 'Allo user'}
          meName={session.me?.name || 'You'}
          onClose={() => setMediaPage(false)}
          onOpen={(e) => openViewer(e.msg, e.index)}
        />
      )}

      {viewer && <MediaViewer view={viewer} onClose={() => setViewer(null)} />}

      {expired ? (
        <div className="gs-dead">
          This link has expired. The conversation has been deleted.
        </div>
      ) : blocked ? (
        /* 🟥 SAY IT, RATHER THAN LETTING SENDS FAIL QUIETLY. Between two Allo
           accounts a blocked sender is deliberately not told — they still have
           an app, other conversations, and a life outside this one. A guest has
           none of that: this page IS the whole relationship, so a composer that
           silently swallows every message would just look broken, and they would
           keep retyping it. The history stays readable until the link expires,
           because taking it away is a separate decision the owner has not made. */
        <div className="gs-dead gs-blocked">
          <Slash width={17} height={17} />
          <span>
            You can no longer send messages in this chat.
            {' '}You can still read what was shared until the link expires.
          </span>
        </div>
      ) : (
        <div className="gs-composer">
          {sendErr && (
            <div className="gs-senderr" role="alert" onClick={() => setSendErr('')}>
              {sendErr}
            </div>
          )}

          {/* ── where you are, before you say so ── */}
          {place && (
            <div className="gs-place">
              {place.loading ? (
                <div className="gs-place-wait"><span className="gs-spin sm" />Finding you…</div>
              ) : (
                <>
                  <StaticMap lat={place.lat} lng={place.lng} zoom={15} height={140} />
                  <div className="gs-place-row">
                    <MapPin width={16} height={16} />
                    <div className="gs-place-txt">
                      <b>{place.naming ? 'Looking up the place…' : (place.name || 'Current location')}</b>
                      <i>{place.lat.toFixed(4)}, {place.lng.toFixed(4)}</i>
                    </div>
                    <button className="gs-tray-cancel" onClick={() => setPlace(null)}>Cancel</button>
                    <button className="gs-send" onClick={sendPlace} aria-label="Send location">
                      <Send width={18} height={18} />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── what you are answering, before you answer it ── */}
          {replyTo && (
            <div className="gs-replybar">
              <span className="gs-replybar-bar" />
              <div className="gs-replybar-txt">
                <b>{replyTo.senderId === session.guestUid ? 'You' : (session.ownerName || 'Allo user')}</b>
                <span>{replySnippet({ text: replyTo._plain || replyTo.text, type: replyTo.type })}</span>
              </div>
              <button className="gs-icon" onClick={() => setReplyTo(null)} aria-label="Cancel reply">
                <X width={14} height={14} />
              </button>
            </div>
          )}

          {/* ── the link card, before it goes ──
              wrapped, because the composer is a flex ROW and .lc.strip does not
              shrink — see the note on .gs-linkstrip */}
          {link && !tray.length && !place && !memo && (
            <div className="gs-linkstrip">
              <LinkCard
                meta={link.meta}
                onClose={() => { linkDismissed.current.add(link.url); setLink(null); }}
              />
            </div>
          )}

          {/* ── the review tray: what you picked, before it goes ── */}
          {tray.length > 0 && (
            <div className="gs-tray">
              <div className="gs-tray-strip">
                {tray.map((w, i) => {
                  const f = w.file;
                  const { ext, color } = docStyle(f.name);
                  return (
                    <div className="gs-tray-item" key={w.key}>
                      {f.type?.startsWith('image/') ? (
                        <img src={w.previewUrl} alt="" />
                      ) : f.type?.startsWith('video/') ? (
                        <video src={w.previewUrl} muted preload="metadata" />
                      ) : w.page ? (
                        /* 🟢 THE ACTUAL FIRST PAGE, before you send it. A filename
                           tells you which file you picked; the page tells you
                           whether it is the right one. */
                        <img className="gs-tray-page" src={w.page} alt={`First page of ${f.name}`} />
                      ) : (
                        <span className="gs-tray-doc" style={{ '--doc': color }}>
                          {w.pageBusy
                            ? <span className="gs-st-spin" aria-label="Rendering the first page" />
                            : <DocText width={18} height={18} />}
                          <b>{ext || 'FILE'}</b>
                          <i>{f.name}</i>
                        </span>
                      )}
                      <button className="gs-tray-x" onClick={() => dropFromTray(i)} aria-label="Remove">
                        <X width={11} height={11} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="gs-tray-row">
                <input
                  className="gs-tray-cap" value={trayCap} placeholder="Add a caption…"
                  onChange={(e) => setTrayCap(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendTray(); } }}
                />
                <button className="gs-tray-cancel" onClick={clearTray}>Cancel</button>
                <button className="gs-send" onClick={sendTray} aria-label="Send">
                  <Send width={18} height={18} />
                </button>
              </div>
            </div>
          )}

          {/* ── recording takes the whole bar over, so the only two things you can
                 do are the two that make sense ── */}
          {memo ? (
            <div className="gs-recbar">
              <audio className="gs-memo-audio" src={memo.url} controls preload="metadata" />
              <div className="gs-wave gs-memo-wave" aria-hidden="true">
                {memo.waveform.slice(0, 32).map((v, i) => (
                  <i key={i} style={{ height: `${Math.max(8, Math.round(v * 100))}%` }} />
                ))}
              </div>
              {/* 🟢 A BIN, NOT THE WORD. Next to a round Send button the word
                     "Delete" read as the heavier of the two — and on a narrow
                     phone it pushed the waveform out of the bar. The icon is
                     the same one the message action bar uses to mean exactly
                     this, so it needs no explaining; the label survives for
                     screen readers. */}
              <button
                className="gs-memo-del" onClick={dropMemo}
                title="Delete recording" aria-label="Delete recording"
              >
                <Trash width={17} height={17} />
              </button>
              <button className="gs-send" onClick={sendMemo} aria-label="Send recording">
                <Send width={18} height={18} />
              </button>
            </div>
          ) : rec ? (
            <div className="gs-recbar">
              <span className="gs-rec-dot" />
              <b>{Math.floor(rec.ms / 60000)}:{String(Math.floor(rec.ms / 1000) % 60).padStart(2, '0')}</b>
              <span className="gs-rec-hint">Recording…</span>
              <button className="gs-tray-cancel" onClick={() => stopRec(true)}>Cancel</button>
              <button className="gs-send" onClick={() => stopRec(false)} aria-label="Send recording">
                <Send width={18} height={18} />
              </button>
            </div>
          ) : (
            <>
              <button className="gs-icon" onClick={() => setSheet((v) => !v)} aria-label="Attach">
                <Paperclip width={19} height={19} />
              </button>

              {/* one hidden input per SOURCE, because `capture` is what makes a phone
                  open the camera rather than the gallery, and it cannot be toggled
                  on a single element after the fact */}
              {/* 🟥 ONE MEDIA TYPE, OR `capture` IS IGNORED. This asked for
                     `image/*,video/*` — and a browser handed two capture
                     intents cannot choose between them, so it stops trying and
                     falls back to the ordinary file chooser: you tap Camera and
                     the phone opens the gallery. Android Chrome does this
                     reliably; iOS Safari degrades to the Photo Library /
                     Take Photo action sheet, which is the same complaint one
                     tap later. Naming a single type is what makes the camera
                     open directly. Video is not lost — Gallery still accepts
                     it, and a video shot from the sheet is one extra tap
                     rather than a camera that never appears. */}
              <input ref={camRef} type="file" hidden accept="image/*" capture="environment"
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />
              <input ref={fileRef} type="file" hidden multiple accept="image/*,video/*"
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />
              <input ref={docRef} type="file" hidden multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rtf"
                onChange={(e) => { stageFiles(e.target.files); e.target.value = ''; }} />

              {sheet && (
                <>
                  <div className="gs-sheet-veil" onClick={() => setSheet(false)} />
                  <div className="gs-sheet" role="menu">
                    <button onClick={() => camRef.current?.click()}>
                      <span className="gs-sheet-ico gs-i-cam"><ImageIcon width={19} height={19} /></span>Camera
                    </button>
                    <button onClick={() => fileRef.current?.click()}>
                      <span className="gs-sheet-ico gs-i-gal"><ImageIcon width={19} height={19} /></span>Gallery
                    </button>
                    <button onClick={() => docRef.current?.click()}>
                      <span className="gs-sheet-ico gs-i-doc"><DocText width={19} height={19} /></span>Document
                    </button>
                    <button onClick={pickPlace}>
                      <span className="gs-sheet-ico gs-i-loc"><MapPin width={19} height={19} /></span>Current location
                    </button>
                  </div>
                </>
              )}

              <textarea
                value={draft} rows={1} placeholder="Message"
                onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              {draft.trim() ? (
                <button className="gs-send" onClick={send} disabled={sending} aria-label="Send">
                  <Send width={18} height={18} />
                </button>
              ) : (
                /* the mic stands where Send would be, exactly as on the phone — the
                   button under your thumb is whichever action the box implies */
                <button className="gs-send mic" onClick={startRec} aria-label="Record a voice message">
                  <Mic width={18} height={18} />
                </button>
              )}
            </>
          )}
          {busyPct != null && <div className="gs-up" style={{ width: `${busyPct}%` }} />}
        </div>
      )}
    </div>
  );
}

/* ══ THE ROUTE ═════════════════════════════════════════════════════════════ */
export default function GuestApp() {
  const token = guestTokenFromLocation();
  const [phase, setPhase] = useState('loading');
  const [link, setLink] = useState(null);
  const [session, setSession] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /* 📐 WHAT THE PERSON CAN ACTUALLY SEE.
     🟥 A PHONE HAS TWO VIEWPORTS AND CSS ONLY KNOWS ABOUT THE WRONG ONE. The
        LAYOUT viewport is the page's coordinate space and it does not move; the
        VISUAL viewport is the rectangle of glass not currently covered by the
        keyboard or the browser's own chrome. `position: fixed; inset: 0` pins to
        the layout viewport, so when the keyboard opens the shell keeps its full
        height and the visible window slides down over it — the chat header ends
        up underneath the address bar. That is the reported bug, and no amount of
        vh/dvh fixes it, because dvh describes the browser's UI state, not the
        keyboard's.
     🟢 window.visualViewport is the only thing that reports the real rectangle.
        Its height and its offsetTop go out as CSS variables and .gs-shell is
        positioned from them, so the shell tracks the visible area exactly —
        keyboard open, keyboard closed, address bar collapsing on scroll, and
        rotation.
     🟢 `scroll` matters as much as `resize`. On iOS the keyboard does not resize
        the visual viewport so much as SHIFT it, which arrives as a scroll event
        and no resize at all — listening only for resize fixes Android and leaves
        iOS exactly as broken as before.
     🟢 rAF-coalesced because iOS fires both, repeatedly, mid-animation. */
  useEffect(() => {
    const vv = window.visualViewport;
    let frame = 0;
    const apply = () => {
      frame = 0;
      const root = document.documentElement;
      const h = vv ? vv.height : window.innerHeight;
      // offsetTop is how far the visible box has slid DOWN the layout viewport
      const t = vv ? Math.max(0, vv.offsetTop) : 0;
      root.style.setProperty('--gs-vvh', `${Math.round(h)}px`);
      root.style.setProperty('--gs-vvt', `${Math.round(t)}px`);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(apply); };
    apply();
    if (vv) {
      vv.addEventListener('resize', schedule);
      vv.addEventListener('scroll', schedule);
    }
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      if (vv) {
        vv.removeEventListener('resize', schedule);
        vv.removeEventListener('scroll', schedule);
      }
      window.removeEventListener('orientationchange', schedule);
      window.removeEventListener('resize', schedule);
      /* 🟥 AND TAKE THEM BACK OFF. These live on <html>, which the whole
         document shares — leaving a height behind would size anything that
         later opted into the variable to a stale keyboard. */
      document.documentElement.style.removeProperty('--gs-vvh');
      document.documentElement.style.removeProperty('--gs-vvt');
    };
  }, []);

  useEffect(() => {
    let dead = false;
    (async () => {
      if (!token) { setPhase('error'); return; }

      /* 🟥 ASK BEFORE TOUCHING AUTH. Firebase Auth persistence is per ORIGIN and
         shared by every tab, so on the same host that serves the Allo web app —
         which, on localhost, is exactly where a test link points — signing a
         guest in would sign the OWNER out of their own session, in every tab.
         Nothing here can undo that afterwards, so the only safe move is to stop
         before the first auth call and say why. */
      if (await guestSessionConflict()) { if (!dead) setPhase('conflict'); return; }
      try {
        const l = await peekGuestLink(token);
        if (dead) return;
        setLink(l);
        if (l.status === 'expired') { setPhase('expired'); return; }
        /* 🟥 M6: a SPENT call link went to the join form — name, email, passcode — and only then said
              "already used". 🟢 Said at once. */
        if (l.status === 'used') { setPhase('used'); return; }

        /* ☎️ A CALL LINK IS A DIFFERENT PAGE, decided here and nowhere else.
           🟥 IT MUST BE DECIDED BEFORE THE RESUME BELOW. A call link has no
           chatId, so the saved-session check would never match — but a person
           who used a CHAT link earlier in this browser has one saved, and
           without this branch a call link opened afterwards would resume them
           into that old conversation instead. */
        if (l.kind === 'call') { setPhase('call'); return; }

        /* 🟢 RESUME. Coming back from the email an hour later must land in the
           conversation, not on the form again. The keypair is still in this
           browser, so the history is still readable. */
        const saved = loadGuestSession();
        if (saved && saved.token === token && saved.chatId) {
          await ensureAnonUser();
          if (dead) return;
          setSession(saved);
          setPhase('chat');
          return;
        }
        setPhase('join');
      } catch (e) {
        if (!dead) setPhase(e.code === 'not_found' ? 'gone' : 'error');
      }
    })();
    return () => { dead = true; };
  }, [token]);

  const join = async (form) => {
    setBusy(true); setError('');
    try {
      const s = await joinGuestLink({ token, ...form });
      setSession(s);
      setPhase('chat');
    } catch (e) {
      setError(
        e.code === 'bad_passcode' ? 'That passcode is not right.'
          : e.code === 'expired' ? 'This link has expired.'
            : e.code === 'already_claimed' ? 'This link has already been used by someone else.'
              : e.code === 'account_exists' ? 'This browser is signed in to an Allo account. Open the link in a private window to join as a guest.'
              // you already have a live conversation with this person — one at a
              // time, because both would resolve to the same chat (see /join)
              : e.code === 'already_guest' ? 'You already have a conversation open with them. Use the link you were given first.'
              : e.code === 'owner_cannot_join' || e.code === 'unauthenticated'
                ? 'This link has to be opened as a guest. If you are signed in to Allo in this browser, open it in a private window instead.'
              : e.code === 'email_invalid' ? 'Please check the email address.'
                : 'Something went wrong. Try again.',
      );
    }
    setBusy(false);
  };

  if (phase === 'loading') return <div className="gs-full"><div className="gs-spin" /></div>;
  if (phase === 'chat' && session) {
    return <GuestChat session={session} onExpired={() => { endGuestSession(); setPhase('expired'); }} />;
  }
  if (phase === 'call' && link) return <GuestCall link={link} token={token} />;
  if (phase === 'join' && link) return <JoinScreen link={link} onJoin={join} busy={busy} error={error} />;

  if (phase === 'conflict') {
    return (
      <div className="gs-full">
        <div className="gs-join-card gs-msg">
          <Lock width={26} height={26} />
          <b>You&rsquo;re signed in to Allo in this browser</b>
          <p>
            A guest link has to open as its own person, and a browser can only hold one
            Allo session at a time — opening it here would sign you out of your account
            in every tab.
          </p>
          <p>
            Open this link in a <b>private / incognito window</b>, or in a different
            browser. That is also how it will work for whoever you sent it to, since
            they are not signed in to Allo at all.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="gs-full">
      <div className="gs-join-card gs-msg">
        <Timer width={26} height={26} />
        <b>{phase === 'expired' ? 'This link has expired' : phase === 'used' ? 'This link has been used' : phase === 'gone' ? 'This link does not exist' : 'Something went wrong'}</b>
        <p>
          {phase === 'used'
            ? 'This link was for one call, and that call has happened. Ask for a new link if you need to talk again.'
            : phase === 'expired' && link && link.kind === 'call'
              ? 'This call link has ended. Ask for a new one if you still need to talk.'
              : phase === 'expired'
                ? 'The conversation and everything in it have been deleted, as promised. Ask for a new link if you still need to talk.'
                : 'Check the link you were sent, or ask for a new one.'}
        </p>
      </div>
    </div>
  );
}
